import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './StudentProfile.css';
import { API_BASE, DEFAULT_AVATAR } from '../config';
import { authFetch } from '../utils/api';
import { useToast } from './Toast';
import DateFilterModal from './DateFilterModal';

// Helper: safely parse a mark value to a number, returning null for non-numeric values like 'A', '-'
const parseNumericMark = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
};

// Helper: display a mark value — shows 0 as '0', null/undefined as fallback
const displayMark = (val, fallback = '-') => {
  if (val === null || val === undefined || val === '') return fallback;
  return val;
};

const displayMarkWithTotal = (obtained, total, fallback = '-') => {
  const shownObtained = displayMark(obtained, fallback);
  if (total === null || total === undefined || total === '') {
    return shownObtained === fallback ? fallback : `${shownObtained}/N/A`;
  }
  return `${shownObtained}/${total}`;
};

const toPercentage = (obtained, total) => {
  return null;
};

const isAbsentMark = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === 'a' || normalized === 'ab' || normalized === 'ta';
};

const getAbsentMarkLabel = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return isAbsentMark(value) ? String(value).trim().toUpperCase() : null;
};

const formatChartValue = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
};

const reportSubjectDisplayPriority = ['physics', 'chemistry', 'maths', 'biology'];
const normalizeReportSubjectKey = (subject) => {
  const normalized = String(subject || '').trim().toLowerCase();
  if (normalized === 'mathematics' || normalized === 'math') return 'maths';
  return normalized;
};

const chartAxisStyle = {
  tick: { fontSize: 12, fill: '#2d3748' },
  axisLine: { stroke: '#94a3b8' },
  tickLine: { stroke: '#94a3b8' }
};

const chartTooltipStyle = {
  contentStyle: {
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    color: '#1e293b',
    fontSize: '12px'
  },
  itemStyle: { color: '#1e293b' },
  labelStyle: { color: '#334155', fontWeight: 600 }
};

const StudentProfile = ({
  student,
  batchStats,
  onBack,
  autoGeneratePdf = false,
  onBulkPdfReady = null,
  onBulkPdfError = null,
  bulkPdfDateFrom = null,
  bulkPdfDateTo = null,
}) => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [dailyTests, setDailyTests] = useState([]);
  const [mockTests, setMockTests] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  const [studentMetrics, setStudentMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [studentTestInsights, setStudentTestInsights] = useState({ daily: [], mock: [], combined_latest: [] });
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightTab, setInsightTab] = useState('mock');
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [dailyDateFrom, setDailyDateFrom] = useState('');
  const [dailyDateTo, setDailyDateTo] = useState('');
  const [mockDateFrom, setMockDateFrom] = useState('');
  const [mockDateTo, setMockDateTo] = useState('');
  const [dailyChartType, setDailyChartType] = useState('line');
  const [mockChartType, setMockChartType] = useState('grouped');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const [showDateFilterModal, setShowDateFilterModal] = useState(false);
  const [pdfDateFrom, setPdfDateFrom] = useState(null);
  const [pdfDateTo, setPdfDateTo] = useState(null);
  const [pdfExportPending, setPdfExportPending] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState({
    date: new Date().toISOString().split('T')[0],
    teacherFeedback: '',
    suggestions: '',
    academicDirectorSignature: '',
    studentSignature: '',
    parentSignature: ''
  });
  const reportExportRef = useRef(null);
  const autoPdfTriggeredRef = useRef(false);

  // Resolve identifiers from various possible props
  const studentNo = student?.studentNo || student?.student_no || null;
  const studentId = student?.rollNo || student?.admissionNo || student?.student_id || null;

  // Fetch complete student data from API
  useEffect(() => {
    if (studentNo) {
      fetchStudentData();
    }
  }, [studentNo]);

  const fetchStudentData = async () => {
    setLoading(true);
    setError('');
    const isBulkAutoMode = autoGeneratePdf && !!onBulkPdfReady;
    
    try {
      const response = await authFetch(`${API_BASE}/api/student/${studentNo}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch student data: ${response.statusText}`);
      }
      
      const data = await response.json();
      setStudentData(data);

      // Fetch analysis + per-test insights for report content.
      // In bulk PDF auto mode, skip only metrics to reduce load.
      setMetricsLoading(!isBulkAutoMode);
      setInsightsLoading(true);
      const requestList = [
        authFetch(`${API_BASE}/api/analysis/individual/${studentNo}`),
        authFetch(`${API_BASE}/api/analysis/student-test-insights/${studentNo}?test_type=both&limit=6`)
      ];
      if (!isBulkAutoMode) {
        requestList.push(authFetch(`${API_BASE}/api/analysis/student-metrics/${studentNo}`));
      }
      const settledRequests = await Promise.allSettled(requestList);
      const analysisReq = settledRequests[0];
      const insightsReq = settledRequests[1];
      const metricsReq = isBulkAutoMode ? null : settledRequests[2];

      // Analysis bundle
      try {
        if (analysisReq.status === 'fulfilled' && analysisReq.value.ok) {
          const analysisResult = await analysisReq.value.json();
          setAnalysisData(analysisResult);
          setDailyTests(analysisResult.daily_tests || []);
          setMockTests(analysisResult.mock_tests || []);
          setFeedbackList(analysisResult.feedback || []);
        } else {
          setAnalysisData(null);
          setDailyTests([]);
          setMockTests([]);
          await fetchFeedback();
        }
      } catch (err) {
        console.error('Error resolving analysis data:', err);
        setAnalysisData(null);
        setDailyTests([]);
        setMockTests([]);
        await fetchFeedback();
      }

      // Per-test insights (required for remediation section)
      try {
        if (insightsReq && insightsReq.status === 'fulfilled' && insightsReq.value.ok) {
          const insightsResult = await insightsReq.value.json();
          setStudentTestInsights(insightsResult.insights || { daily: [], mock: [], combined_latest: [] });
        } else {
          setStudentTestInsights({ daily: [], mock: [], combined_latest: [] });
        }
      } catch (err) {
        console.error('Error fetching student test insights:', err);
        setStudentTestInsights({ daily: [], mock: [], combined_latest: [] });
      } finally {
        setInsightsLoading(false);
      }

      // Metrics (skip in bulk auto mode)
      if (!isBulkAutoMode) {
        try {
          if (metricsReq && metricsReq.status === 'fulfilled' && metricsReq.value.ok) {
            const metricsResult = await metricsReq.value.json();
            setStudentMetrics(metricsResult);
          } else {
            setStudentMetrics(null);
          }
        } catch (err) {
          console.error('Error fetching student metrics:', err);
          setStudentMetrics(null);
        } finally {
          setMetricsLoading(false);
        }

      } else {
        setStudentMetrics(null);
        setMetricsLoading(false);
      }
      
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError(err.message);
      setMetricsLoading(false);
      setInsightsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/analysis/feedback/${studentNo}`);
      if (response.ok) {
        const data = await response.json();
        setFeedbackList(data.feedback || []);
      }
    } catch (err) {
      console.error('Error fetching feedback:', err);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      toast.warning('Please select a valid image file (JPG, PNG, GIF, WEBP).');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(`${API_BASE}/api/student/${studentNo}/photo`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload photo');
      }

      // Refresh student data to get the new photo URL
      await fetchStudentData();
      toast.success('Photo uploaded successfully!');
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error(err.message || 'Error uploading photo');
    } finally {
      setUploadingPhoto(false);
      // Clear the input
      e.target.value = null;
    }
  };

  const handlePhotoDelete = async () => {
    if (!studentData?.photo_url) {
      toast.warning('No photo to delete.');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this profile photo?')) {
      return;
    }

    setDeletingPhoto(true);
    try {
      const response = await authFetch(`${API_BASE}/api/student/${studentNo}/photo`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete photo');
      }

      // Refresh student data to show default avatar
      await fetchStudentData();
      toast.success('Photo deleted successfully!');
    } catch (err) {
      console.error('Error deleting photo:', err);
      toast.error(err.message || 'Error deleting photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN');
    } catch {
      return dateString;
    }
  };

  // Prepare display data from fetched API data
  const safeStudentData = studentData || {};
  const displayData = {
    // Basic Information
    name: safeStudentData.student_name || 'N/A',
    dob: formatDate(safeStudentData.dob),
    grade: safeStudentData.grade || 'N/A',
    community: safeStudentData.community || 'N/A',
    academicYear: safeStudentData.enrollment_year || 'N/A',
    course: safeStudentData.course || 'N/A',
    board: safeStudentData.board || 'N/A',
    rollNo: safeStudentData.student_id || 'N/A',
    gender: safeStudentData.gender || 'N/A',

    // Contact Information
    studentMobile: safeStudentData.student_mobile || 'N/A',
    aadharNumber: safeStudentData.aadhar_no || 'N/A',
    aasarId: safeStudentData.apaar_id || 'N/A',
    emailId: safeStudentData.email || 'N/A',

    // Personal Information
    schoolName: safeStudentData.school_name || 'N/A',
    guardianName: safeStudentData.guardian_name || 'N/A',
    guardianOccupation: safeStudentData.guardian_occupation || 'N/A',
    guardianContact: safeStudentData.guardian_mobile || 'N/A',
    guardianEmail: safeStudentData.guardian_email || 'N/A',
    fatherName: safeStudentData.father_name || 'N/A',
    fatherOccupation: safeStudentData.father_occupation || 'N/A',
    fatherContact: safeStudentData.father_mobile || 'N/A',
    fatherEmail: safeStudentData.father_email || 'N/A',
    motherName: safeStudentData.mother_name || 'N/A',
    motherOccupation: safeStudentData.mother_occupation || 'N/A',
    motherContact: safeStudentData.mother_mobile || 'N/A',
    motherEmail: safeStudentData.mother_email || 'N/A',
    siblingName: safeStudentData.sibling_name || 'N/A',
    siblingGrade: safeStudentData.sibling_grade || 'N/A',
    siblingSchool: safeStudentData.sibling_school || 'N/A',
    siblingCollege: safeStudentData.sibling_college || 'N/A',

    // 10th Standard Marks
    std10Marks: {
      schoolName: safeStudentData.tenth_school_name || 'N/A',
      yearOfPassing: safeStudentData.tenth_year_of_passing || 'N/A',
      boardOfStudy: safeStudentData.tenth_board_of_study || 'N/A',
      english: displayMark(safeStudentData.tenth_english),
      tamil: displayMark(safeStudentData.tenth_tamil),
      hindi: displayMark(safeStudentData.tenth_hindi),
      maths: displayMark(safeStudentData.tenth_maths),
      science: displayMark(safeStudentData.tenth_science),
      socialScience: displayMark(safeStudentData.tenth_social_science),
      total: displayMark(safeStudentData.tenth_total_marks)
    },

    // 12th Standard Marks
    std12Marks: {
      schoolName: safeStudentData.twelfth_school_name || 'N/A',
      yearOfPassing: safeStudentData.twelfth_year_of_passing || 'N/A',
      boardOfStudy: safeStudentData.twelfth_board_of_study || 'N/A',
      english: displayMark(safeStudentData.twelfth_english),
      tamil: displayMark(safeStudentData.twelfth_tamil),
      physics: displayMark(safeStudentData.twelfth_physics),
      chemistry: displayMark(safeStudentData.twelfth_chemistry),
      mathematics: displayMark(safeStudentData.twelfth_maths),
      biology: displayMark(safeStudentData.twelfth_biology),
      computerScience: displayMark(safeStudentData.twelfth_computer_science),
      total: displayMark(safeStudentData.twelfth_total_marks)
    },

    // Entrance Exam Marks
    entrance_exam_1: safeStudentData.entrance_exam_1 || '',
    entrance_exam_1_percentile: safeStudentData.entrance_exam_1_percentile || '',
    entrance_exam_1_mark: safeStudentData.entrance_exam_1_mark || '',
    entrance_exam_2: safeStudentData.entrance_exam_2 || '',
    entrance_exam_2_percentile: safeStudentData.entrance_exam_2_percentile || '',
    entrance_exam_2_mark: safeStudentData.entrance_exam_2_mark || '',
    entrance_exam_3: safeStudentData.entrance_exam_3 || '',
    entrance_exam_3_percentile: safeStudentData.entrance_exam_3_percentile || '',
    entrance_exam_3_mark: safeStudentData.entrance_exam_3_mark || '',

    // Counselling Details
    counselling1: {
      forum: safeStudentData.counselling_forum_1 || 'N/A',
      round: safeStudentData.counselling_round_1 || 'N/A',
      allIndiaRank: safeStudentData.all_india_rank_1 || 'N/A',
      communityRank: safeStudentData.community_rank_1 || 'N/A',
      collegeAlloted: safeStudentData.counselling_college_1 || 'N/A'
    },
    counselling2: {
      forum: safeStudentData.counselling_forum_2 || 'N/A',
      round: safeStudentData.counselling_round_2 || 'N/A',
      allIndiaRank: safeStudentData.all_india_rank_2 || 'N/A',
      communityRank: safeStudentData.community_rank_2 || 'N/A',
      collegeAlloted: safeStudentData.counselling_college_2 || 'N/A'
    },
    counselling3: {
      forum: safeStudentData.counselling_forum_3 || 'N/A',
      round: safeStudentData.counselling_round_3 || 'N/A',
      allIndiaRank: safeStudentData.all_india_rank_3 || 'N/A',
      communityRank: safeStudentData.community_rank_3 || 'N/A',
      collegeAlloted: safeStudentData.counselling_college_3 || 'N/A'
    }
  };

  const reportProgram = analysisData?.student?.batch_type || displayData.course;
  const normalizedReportBatchType = String(analysisData?.student?.batch_type || '').trim().toLowerCase();
  const isDailyOnlyProgressReport = (normalizedReportBatchType === 'other' || normalizedReportBatchType === 'board') && (!mockTests || mockTests.length === 0);

  // Apply date filters to tests
  const filteredDailyTests = dailyTests.filter(test => {
    if (!test.test_date) return true;
    const d = test.test_date;
    if (dailyDateFrom && d < dailyDateFrom) return false;
    if (dailyDateTo && d > dailyDateTo) return false;
    return true;
  });

  const filteredMockTests = mockTests.filter(test => {
    if (!test.test_date) return true;
    const d = test.test_date;
    if (mockDateFrom && d < mockDateFrom) return false;
    if (mockDateTo && d > mockDateTo) return false;
    return true;
  });

  const chronologicalMockTests = useMemo(() => {
    return [...(filteredMockTests || [])].sort((a, b) => {
      const aTime = a?.test_date ? new Date(a.test_date).getTime() : 0;
      const bTime = b?.test_date ? new Date(b.test_date).getTime() : 0;
      return aTime - bTime;
    });
  }, [filteredMockTests]);

  const hasAbsentInMockTestRecord = useCallback((test) => {
    const markFields = [
      test?.total_marks,
      test?.maths_marks,
      test?.physics_marks,
      test?.chemistry_marks,
      test?.biology_marks
    ];
    return markFields.some((value) => isAbsentMark(value));
  }, []);

  // Build performance trend from filtered unit tests
  const buildPerformanceTrend = () => {
    if (!filteredDailyTests || filteredDailyTests.length === 0) return [];
    const hasDailyTotals = filteredDailyTests.some(test => parseNumericMark(test.subject_total_marks ?? test.test_total_marks) !== null);
    const byDate = {};
    filteredDailyTests.forEach(test => {
      const d = test.test_date || 'Unknown';
      if (!byDate[d]) byDate[d] = { marks: [], classAvg: [], hasAbsent: false };
      const effectiveTotal = test.subject_total_marks ?? test.test_total_marks;
      const numMark = hasDailyTotals
        ? (toPercentage(test.marks, effectiveTotal) ?? parseNumericMark(test.marks))
        : parseNumericMark(test.marks);
      const numAvg = hasDailyTotals
        ? (toPercentage(test.class_avg, effectiveTotal) ?? parseNumericMark(test.class_avg))
        : parseNumericMark(test.class_avg);

      if (numMark !== null) {
        byDate[d].marks.push(numMark);
      } else if (isAbsentMark(test.marks)) {
        byDate[d].marks.push(0);
        byDate[d].hasAbsent = true;
      }
      if (numAvg !== null) byDate[d].classAvg.push(numAvg);
    });
    return Object.entries(byDate).sort().map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      score: data.marks.length > 0 ? Math.round(data.marks.reduce((a, b) => a + b, 0) / data.marks.length) : null,
      classAvg: data.classAvg.length > 0 ? Math.round(data.classAvg.reduce((a, b) => a + b, 0) / data.classAvg.length) : null,
      unit: 'marks',
      absent: data.hasAbsent
    }));
  };

  // Build monthly test chart data from filtered monthly tests
  const buildMockTestChartData = () => {
    if (!chronologicalMockTests || chronologicalMockTests.length === 0) return [];
    const hasMockSubjectTotals = chronologicalMockTests.some(test =>
      parseNumericMark(test.maths_total_marks) !== null ||
      parseNumericMark(test.physics_total_marks) !== null ||
      parseNumericMark(test.chemistry_total_marks) !== null ||
      parseNumericMark(test.biology_total_marks) !== null
    );

    const getSubjectValue = (obtained, total) => {
      if (!hasMockSubjectTotals) return parseNumericMark(obtained);
      return toPercentage(obtained, total) ?? parseNumericMark(obtained);
    };

    return chronologicalMockTests.map((test, idx) => {
      const isAbsent = hasAbsentInMockTestRecord(test);
      return {
        exam: `Monthly ${idx + 1} (${test.test_date ? new Date(test.test_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''})`,
        physics: isAbsent ? 0 : getSubjectValue(test.physics_marks, test.physics_total_marks),
        chemistry: isAbsent ? 0 : getSubjectValue(test.chemistry_marks, test.chemistry_total_marks),
        biology: isAbsent ? 0 : getSubjectValue(test.biology_marks, test.biology_total_marks),
        maths: isAbsent ? 0 : getSubjectValue(test.maths_marks, test.maths_total_marks),
        total: isAbsent ? 0 : getSubjectValue(test.total_marks, test.test_total_marks),
        unit: 'marks',
        absent: isAbsent
      };
    });
  };

  const buildMockTrendData = () => {
    if (!chronologicalMockTests || chronologicalMockTests.length === 0) return [];
    const hasMockTotal = chronologicalMockTests.some(test => parseNumericMark(test.test_total_marks) !== null);
    return chronologicalMockTests.map((test, idx) => {
      const isAbsent = hasAbsentInMockTestRecord(test);
      return {
        exam: `Monthly ${idx + 1}`,
        date: test.test_date ? new Date(test.test_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-',
        total: isAbsent
          ? 0
          : (hasMockTotal
          ? (toPercentage(test.total_marks, test.test_total_marks) ?? parseNumericMark(test.total_marks))
          : parseNumericMark(test.total_marks)),
        classAvg: hasMockTotal
          ? (toPercentage(test.class_avg_total, test.test_total_marks) ?? parseNumericMark(test.class_avg_total))
          : parseNumericMark(test.class_avg_total),
        topScore: hasMockTotal
          ? (toPercentage(test.top_score_total, test.test_total_marks) ?? parseNumericMark(test.top_score_total))
          : parseNumericMark(test.top_score_total),
        unit: 'marks',
        absent: isAbsent
      };
    });
  };

  const buildLatestMockSubjectShare = () => {
    if (!chronologicalMockTests || chronologicalMockTests.length === 0) return [];
    const lastMock = chronologicalMockTests[chronologicalMockTests.length - 1];
    const hasMockSubjectTotals =
      parseNumericMark(lastMock.maths_total_marks) !== null ||
      parseNumericMark(lastMock.physics_total_marks) !== null ||
      parseNumericMark(lastMock.chemistry_total_marks) !== null ||
      parseNumericMark(lastMock.biology_total_marks) !== null;

    const getLatestValue = (obtained, total) => {
      if (!hasMockSubjectTotals) return parseNumericMark(obtained);
      return toPercentage(obtained, total) ?? parseNumericMark(obtained);
    };

    if (hasAbsentInMockTestRecord(lastMock)) return [];

    const rows = [
      { name: 'Maths', value: getLatestValue(lastMock.maths_marks, lastMock.maths_total_marks), fill: '#f1ed08' },
      { name: 'Physics', value: getLatestValue(lastMock.physics_marks, lastMock.physics_total_marks), fill: '#FF6B9D' },
      { name: 'Chemistry', value: getLatestValue(lastMock.chemistry_marks, lastMock.chemistry_total_marks), fill: '#4A90E2' },
      { name: 'Biology', value: getLatestValue(lastMock.biology_marks, lastMock.biology_total_marks), fill: '#00D9C0' }
    ];
    return rows.filter(r => r.value > 0);
  };

  const formatMockTopics = (exam) => {
    const topics = [
      { label: 'Maths', units: exam.maths_unit_names },
      { label: 'Physics', units: exam.physics_unit_names },
      { label: 'Chemistry', units: exam.chemistry_unit_names },
      { label: 'Biology', units: exam.biology_unit_names },
    ];

    const formatted = topics
      .map(({ label, units }) => {
        const list = Array.isArray(units)
          ? units.filter(Boolean)
          : (units ? [units] : []);
        if (list.length === 0) return null;
        return `${label}: ${list.join(', ')}`;
      })
      .filter(Boolean);

    return formatted.length > 0 ? formatted.join(' | ') : 'N/A';
  };

  const performanceTrend = buildPerformanceTrend();
  const mockTestChartData = buildMockTestChartData();
  const mockTrendData = buildMockTrendData();
  const latestMockSubjectShare = buildLatestMockSubjectShare();

  // Resolve effective PDF date filters (props for bulk, state for individual)
  const effectivePdfDateFrom = bulkPdfDateFrom || pdfDateFrom;
  const effectivePdfDateTo = bulkPdfDateTo || pdfDateTo;

  // Create date-filtered versions of tests for PDF report pages
  const pdfFilteredDailyTests = useMemo(() => {
    if (!effectivePdfDateFrom && !effectivePdfDateTo) return dailyTests;
    return (dailyTests || []).filter(test => {
      if (!test.test_date) return true;
      const d = test.test_date;
      if (effectivePdfDateFrom && d < effectivePdfDateFrom) return false;
      if (effectivePdfDateTo && d > effectivePdfDateTo) return false;
      return true;
    });
  }, [dailyTests, effectivePdfDateFrom, effectivePdfDateTo]);

  const pdfFilteredMockTests = useMemo(() => {
    if (!effectivePdfDateFrom && !effectivePdfDateTo) return mockTests;
    return (mockTests || []).filter(test => {
      if (!test.test_date) return true;
      const d = test.test_date;
      if (effectivePdfDateFrom && d < effectivePdfDateFrom) return false;
      if (effectivePdfDateTo && d > effectivePdfDateTo) return false;
      return true;
    });
  }, [mockTests, effectivePdfDateFrom, effectivePdfDateTo]);

  const reportTests = useMemo(() => {
    if (!pdfFilteredMockTests || pdfFilteredMockTests.length === 0) return [];
    return [...pdfFilteredMockTests]
      .filter((test) => test?.test_date)
      .sort((a, b) => new Date(a.test_date) - new Date(b.test_date));
  }, [pdfFilteredMockTests]);

  const reportSubjectMeta = useMemo(() => ({
    maths: { label: 'Maths', color: '#f1ed08' },
    physics: { label: 'Physics', color: '#3366ff' },
    chemistry: { label: 'Chemistry', color: '#00b894' },
    biology: { label: 'Biology', color: '#ff1744' }
  }), []);

  const formatReportDateShort = useCallback((dateValue, fallback = 'N/A') => {
    if (!dateValue) return fallback;
    try {
      return new Date(dateValue).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch {
      return fallback;
    }
  }, []);

  const buildDailyAxisLabel = useCallback((dateValue, subjectKey, fallbackLabel) => {
    const dateLabel = formatReportDateShort(dateValue, null);
    const subjectLabel = subjectKey ? (reportSubjectMeta[subjectKey]?.label || subjectKey) : null;

    if (dateLabel && subjectLabel) return `${dateLabel} • ${subjectLabel}`;
    if (dateLabel) return dateLabel;
    if (subjectLabel) return subjectLabel;

    return fallbackLabel;
  }, [formatReportDateShort, reportSubjectMeta]);

  const reportSubjectKeys = useMemo(() => {
    const normalizedFromBatch = (analysisData?.student?.batch_subjects || [])
      .map(normalizeReportSubjectKey)
      .filter((key) => ['maths', 'physics', 'chemistry', 'biology'].includes(key));

    const uniqueFromBatch = [...new Set(normalizedFromBatch)]
      .sort((a, b) => reportSubjectDisplayPriority.indexOf(a) - reportSubjectDisplayPriority.indexOf(b));
    if (uniqueFromBatch.length >= 3) return uniqueFromBatch;

    const presentInMock = reportSubjectDisplayPriority.filter((key) =>
      reportTests.some((test) => parseNumericMark(test[`${key}_marks`]) !== null)
    );

    const merged = [...new Set([...uniqueFromBatch, ...presentInMock])]
      .sort((a, b) => reportSubjectDisplayPriority.indexOf(a) - reportSubjectDisplayPriority.indexOf(b));
    return merged.length >= 3 ? merged : reportSubjectDisplayPriority.slice(0, 3);
  }, [analysisData, reportTests]);

  const pdfReportSubjectKeys = useMemo(() => {
    if (isDailyOnlyProgressReport) {
      const normalizedFromBatch = (analysisData?.student?.batch_subjects || [])
        .map(normalizeReportSubjectKey)
        .filter((key) => reportSubjectDisplayPriority.includes(key));
      const presentInDaily = reportSubjectDisplayPriority.filter((key) =>
        (dailyTests || []).some((test) => normalizeReportSubjectKey(test?.subject) === key)
      );
      const merged = [...new Set([...normalizedFromBatch, ...presentInDaily])]
        .sort((a, b) => reportSubjectDisplayPriority.indexOf(a) - reportSubjectDisplayPriority.indexOf(b));

      return merged.length > 0 ? merged : ['physics', 'chemistry', 'maths'];
    }
    return reportSubjectKeys;
  }, [analysisData, dailyTests, isDailyOnlyProgressReport, reportSubjectKeys]);

  const buildReportPointLabel = (test, index) => {
    if (test?.test_name) return test.test_name;
    return `MT-${index + 1}`;
  };

  const dailySubjectComparisonReportData = useMemo(() => {
    const groupedPoints = {};
    (pdfFilteredDailyTests || []).forEach((test) => {
      if (!test?.test_date || !test?.subject) return;
      const subjectKey = normalizeReportSubjectKey(test.subject);

      if (!pdfReportSubjectKeys.includes(subjectKey)) return;

      const groupKey = `${test.test_date}::${subjectKey}`;
      if (!groupedPoints[groupKey]) {
        groupedPoints[groupKey] = {
          test_date: test.test_date,
          subjectKey,
          scores: [],
          absentLabel: null
        };
      }

      const numericScore = parseNumericMark(test.marks);
      if (numericScore !== null) {
        groupedPoints[groupKey].scores.push(numericScore);
      } else {
        const absentLabel = getAbsentMarkLabel(test.marks);
        if (absentLabel) {
          groupedPoints[groupKey].absentLabel = absentLabel;
        }
      }
    });

    return Object.values(groupedPoints)
      .sort((a, b) => {
        const dateCompare = new Date(a.test_date) - new Date(b.test_date);
        if (dateCompare !== 0) return dateCompare;
        return a.subjectKey.localeCompare(b.subjectKey);
      })
      .map((point, idx) => {
        const hasScores = point.scores.length > 0;
        const numericValue = hasScores
          ? Number((point.scores.reduce((sum, value) => sum + value, 0) / point.scores.length).toFixed(2))
          : 0;
        const displayValue = hasScores ? formatChartValue(numericValue) : point.absentLabel;

        return {
          label: isDailyOnlyProgressReport
            ? buildDailyAxisLabel(point.test_date, point.subjectKey, `UT-${idx + 1}`)
            : `UT-${idx + 1}`,
          value: numericValue,
          value_display: displayValue,
          hasValue: hasScores || Boolean(point.absentLabel),
          subjectKey: point.subjectKey,
          subjectLabel: reportSubjectMeta[point.subjectKey]?.label || point.subjectKey,
          fill: reportSubjectMeta[point.subjectKey]?.color || '#64748b'
        };
      });
  }, [pdfFilteredDailyTests, isDailyOnlyProgressReport, pdfReportSubjectKeys, reportSubjectMeta, buildDailyAxisLabel]);

  const dailySubjectVsClassReportData = useMemo(() => {
    const roundedOrNull = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isNaN(num) ? null : Math.round(num);
    };

    const report = {};
    pdfReportSubjectKeys.forEach((subjectKey) => {
      const subjectTests = (pdfFilteredDailyTests || [])
        .filter((test) => {
          const testSubjectKey = normalizeReportSubjectKey(test?.subject);
          return testSubjectKey === subjectKey && test?.test_date;
        })
        .sort((a, b) => {
          const dateCompare = new Date(a.test_date) - new Date(b.test_date);
          if (dateCompare !== 0) return dateCompare;
          return String(a.unit_name || '').localeCompare(String(b.unit_name || ''));
        });

      report[subjectKey] = subjectTests.map((test, index) => {
        const total = test.subject_total_marks ?? test.test_total_marks;
        const student = toPercentage(test.marks, total) ?? parseNumericMark(test.marks);
        const high = toPercentage(test.top_score, total) ?? parseNumericMark(test.top_score);
        const average = toPercentage(test.class_avg, total) ?? parseNumericMark(test.class_avg);
        const low = toPercentage(test.class_low, total) ?? parseNumericMark(test.class_low);

        return {
          label: buildDailyAxisLabel(test.test_date, subjectKey, `UT-${index + 1}`),
          student: roundedOrNull(student),
          high: roundedOrNull(high),
          average: roundedOrNull(average),
          low: roundedOrNull(low)
        };
      });
    });

    return report;
  }, [pdfFilteredDailyTests, pdfReportSubjectKeys, buildDailyAxisLabel]);

  const dailyTotalComparisonReportData = useMemo(() => {
    const groupedByDate = {};

    (pdfFilteredDailyTests || []).forEach((test) => {
      if (!test?.test_date) return;

      const groupKey = test.test_date;
      if (!groupedByDate[groupKey]) {
        groupedByDate[groupKey] = {
          student: [],
          high: [],
          average: [],
          low: [],
          subjects: new Set()
        };
      }

      const subjectKey = normalizeReportSubjectKey(test.subject);
      if (reportSubjectDisplayPriority.includes(subjectKey)) {
        groupedByDate[groupKey].subjects.add(subjectKey);
      }

      const total = test.subject_total_marks ?? test.test_total_marks;

      const student = toPercentage(test.marks, total) ?? parseNumericMark(test.marks);
      const high = toPercentage(test.top_score, total) ?? parseNumericMark(test.top_score);
      const average = toPercentage(test.class_avg, total) ?? parseNumericMark(test.class_avg);
      const low = toPercentage(test.class_low, total) ?? parseNumericMark(test.class_low);

      if (student !== null) groupedByDate[groupKey].student.push(student);
      if (high !== null) groupedByDate[groupKey].high.push(high);
      if (average !== null) groupedByDate[groupKey].average.push(average);
      if (low !== null) groupedByDate[groupKey].low.push(low);
    });

    const averageOrNull = (values) => {
      if (!values || values.length === 0) return null;
      const avg = values.reduce((sum, value) => sum + value, 0) / values.length;
      return Math.round(avg);
    };

    return Object.entries(groupedByDate)
      .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
      .map(([date, values], index) => {
        const orderedSubjects = [...(values.subjects || [])]
          .sort((a, b) => reportSubjectDisplayPriority.indexOf(a) - reportSubjectDisplayPriority.indexOf(b));
        const subjectText = orderedSubjects.length > 0
          ? orderedSubjects.map((key) => reportSubjectMeta[key]?.label || key).join('/')
          : 'All Subjects';

        return {
          label: buildDailyAxisLabel(date, subjectText, `UT-${index + 1}`),
          student: averageOrNull(values.student),
          high: averageOrNull(values.high),
          average: averageOrNull(values.average),
          low: averageOrNull(values.low)
        };
      });
  }, [pdfFilteredDailyTests, buildDailyAxisLabel, reportSubjectMeta]);

  const totalComparisonReportData = useMemo(() => {
    const roundedOrNull = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isNaN(num) ? null : Math.round(num);
    };

    const sumNumericFields = (test, fields) => {
      let sum = 0;
      let hasAny = false;

      fields.forEach((field) => {
        const val = parseNumericMark(test[field]);
        if (val !== null) {
          sum += val;
          hasAny = true;
        }
      });

      return hasAny ? sum : null;
    };

    return reportTests.map((test, index) => {
      const isAbsent = hasAbsentInMockTestRecord(test);
      const student = isAbsent
        ? 0
        : (parseNumericMark(test.report_student_total)
        ?? sumNumericFields(
        test,
        reportSubjectKeys.map((key) => `${key}_marks`)
      ));
      const high = parseNumericMark(test.report_class_high_total)
        ?? sumNumericFields(
        test,
        reportSubjectKeys.map((key) => `class_high_${key}`)
      );
      const average = parseNumericMark(test.report_class_avg_total)
        ?? sumNumericFields(
        test,
        reportSubjectKeys.map((key) => `class_avg_${key}`)
      );
      const low = parseNumericMark(test.report_class_low_total)
        ?? sumNumericFields(
        test,
        reportSubjectKeys.map((key) => `class_low_${key}`)
      );

      return {
        label: buildReportPointLabel(test, index),
        student: isAbsent ? 0 : roundedOrNull(student),
        high: roundedOrNull(high),
        average: roundedOrNull(average),
        low: roundedOrNull(low),
        absent: isAbsent
      };
    });
  }, [reportTests, reportSubjectKeys, hasAbsentInMockTestRecord]);

  const buildMockSubjectVsClassData = useCallback((subjectKey) => {
    return reportTests.map((test, index) => {
      const isAbsent = hasAbsentInMockTestRecord(test);
      const student = isAbsent
        ? 0
        : (toPercentage(test[`${subjectKey}_marks`], test[`${subjectKey}_total_marks`])
        ?? parseNumericMark(test[`${subjectKey}_marks`]));
      const average = toPercentage(test[`class_avg_${subjectKey}`], test[`${subjectKey}_total_marks`])
        ?? parseNumericMark(test[`class_avg_${subjectKey}`]);
      const high = toPercentage(test[`class_high_${subjectKey}`], test[`${subjectKey}_total_marks`])
        ?? parseNumericMark(test[`class_high_${subjectKey}`]);
      const low = toPercentage(test[`class_low_${subjectKey}`], test[`${subjectKey}_total_marks`])
        ?? parseNumericMark(test[`class_low_${subjectKey}`]);

      return {
        label: buildReportPointLabel(test, index),
        student: isAbsent ? 0 : (student !== null ? Math.round(student) : null),
        high: high !== null ? Math.round(high) : null,
        average: average !== null ? Math.round(average) : null,
        low: low !== null ? Math.round(low) : null,
        absent: isAbsent
      };
    });
  }, [reportTests, hasAbsentInMockTestRecord]);

  const mockSubjectVsClassReportData = useMemo(() => {
    const report = {};
    reportSubjectKeys.forEach((subjectKey) => {
      report[subjectKey] = buildMockSubjectVsClassData(subjectKey);
    });
    return report;
  }, [reportSubjectKeys, buildMockSubjectVsClassData]);

  const buildMockGroupKey = useCallback((test) => {
    const normalizeArray = (value) => Array.isArray(value) ? value : (value ? [value] : []);
    return [
      test?.test_date || '',
      JSON.stringify(normalizeArray(test?.maths_unit_names)),
      JSON.stringify(normalizeArray(test?.physics_unit_names)),
      JSON.stringify(normalizeArray(test?.chemistry_unit_names)),
      JSON.stringify(normalizeArray(test?.biology_unit_names)),
      test?.maths_total_marks ?? '',
      test?.physics_total_marks ?? '',
      test?.chemistry_total_marks ?? '',
      test?.biology_total_marks ?? '',
      test?.test_total_marks ?? ''
    ].join('::');
  }, []);

  const dailyConductedCount = useMemo(() => {
    const keySet = new Set(
      (dailyTests || []).map((t) => `${t.test_date || ''}::${(t.subject || '').trim().toLowerCase()}::${(t.unit_name || '').trim().toLowerCase() || 'unknown'}`)
    );
    return keySet.size;
  }, [dailyTests]);

  const dailyAttemptedCount = useMemo(() => {
    const keySet = new Set(
      (dailyTests || [])
        .filter((t) => parseNumericMark(t.marks) !== null)
        .map((t) => `${t.test_date || ''}::${(t.subject || '').trim().toLowerCase()}::${(t.unit_name || '').trim().toLowerCase() || 'unknown'}`)
    );
    return keySet.size;
  }, [dailyTests]);

  const mockConductedCount = useMemo(() => {
    const keySet = new Set((mockTests || []).map((t) => buildMockGroupKey(t)));
    return keySet.size;
  }, [mockTests, buildMockGroupKey]);

  const mockAttemptedCount = useMemo(() => {
    const hasAnyNumericInMockTest = (test) => {
      const markFields = [
        test?.total_marks,
        test?.maths_marks,
        test?.physics_marks,
        test?.chemistry_marks,
        test?.biology_marks
      ];
      return markFields.some((value) => parseNumericMark(value) !== null);
    };

    const keySet = new Set(
      (mockTests || [])
        .filter((t) => hasAnyNumericInMockTest(t))
        .map((t) => buildMockGroupKey(t))
    );
    return keySet.size;
  }, [mockTests, buildMockGroupKey]);

  // PDF-specific attendance counts (respects date filter)
  const pdfDailyConductedCount = useMemo(() => {
    const keySet = new Set(
      (pdfFilteredDailyTests || []).map((t) => `${t.test_date || ''}::${(t.subject || '').trim().toLowerCase()}::${(t.unit_name || '').trim().toLowerCase() || 'unknown'}`)
    );
    return keySet.size;
  }, [pdfFilteredDailyTests]);

  const pdfDailyAttemptedCount = useMemo(() => {
    const keySet = new Set(
      (pdfFilteredDailyTests || [])
        .filter((t) => parseNumericMark(t.marks) !== null)
        .map((t) => `${t.test_date || ''}::${(t.subject || '').trim().toLowerCase()}::${(t.unit_name || '').trim().toLowerCase() || 'unknown'}`)
    );
    return keySet.size;
  }, [pdfFilteredDailyTests]);

  const pdfMockConductedCount = useMemo(() => {
    const keySet = new Set((pdfFilteredMockTests || []).map((t) => buildMockGroupKey(t)));
    return keySet.size;
  }, [pdfFilteredMockTests, buildMockGroupKey]);

  const pdfMockAttemptedCount = useMemo(() => {
    const hasAnyNumericInMockTest = (test) => {
      const markFields = [
        test?.total_marks,
        test?.maths_marks,
        test?.physics_marks,
        test?.chemistry_marks,
        test?.biology_marks
      ];
      return markFields.some((value) => parseNumericMark(value) !== null);
    };

    const keySet = new Set(
      (pdfFilteredMockTests || [])
        .filter((t) => hasAnyNumericInMockTest(t))
        .map((t) => buildMockGroupKey(t))
    );
    return keySet.size;
  }, [pdfFilteredMockTests, buildMockGroupKey]);

  const attendanceSummaryRows = useMemo(() => {
    const isDateFiltered = effectivePdfDateFrom || effectivePdfDateTo;
    const weeklyConducted = isDateFiltered ? pdfDailyConductedCount : Number(studentMetrics?.daily_tests_conducted ?? dailyConductedCount);
    const weeklyAttended = isDateFiltered ? pdfDailyAttemptedCount : Number(studentMetrics?.daily_tests_attended ?? dailyAttemptedCount);
    const mockConducted = isDateFiltered ? pdfMockConductedCount : Number(studentMetrics?.mock_tests_conducted ?? mockConductedCount);
    const mockAttended = isDateFiltered ? pdfMockAttemptedCount : Number(studentMetrics?.mock_tests_attended ?? mockAttemptedCount);

    const rows = [
      {
        testType: 'Unit Test',
        conducted: weeklyConducted,
        attended: weeklyAttended,
        summary: `${weeklyAttended}/${weeklyConducted || 0}`
      }
    ];

    if (isDailyOnlyProgressReport) return rows;

    return [
      ...rows,
      {
        testType: 'Monthly Test',
        conducted: mockConducted,
        attended: mockAttended,
        summary: `${mockAttended}/${mockConducted || 0}`
      }
    ];
  }, [studentMetrics, dailyAttemptedCount, dailyConductedCount, mockAttemptedCount, mockConductedCount, isDailyOnlyProgressReport, effectivePdfDateFrom, effectivePdfDateTo, pdfDailyConductedCount, pdfDailyAttemptedCount, pdfMockConductedCount, pdfMockAttemptedCount]);

  const activeInsightRows = useMemo(() => {
    if (insightTab === 'daily') return studentTestInsights?.daily || [];
    return studentTestInsights?.mock || [];
  }, [insightTab, studentTestInsights]);

  const pdfInsightRows = useMemo(() => {
    return (studentTestInsights?.combined_latest || []).slice(0, 6);
  }, [studentTestInsights]);

  const pdfChartMargin = { top: 20, right: 26, left: 30, bottom: 8 };
  const pdfAxisTickStyle = { fontSize: 13, fill: '#374151', fontWeight: 500 };
  const pdfXAxisProps = {
    tick: pdfAxisTickStyle,
    tickMargin: 10,
    axisLine: { stroke: '#d1d5db' },
    tickLine: { stroke: '#d1d5db' },
    padding: { left: 24, right: 18 }
  };
  const pdfYAxisProps = {
    tick: pdfAxisTickStyle,
    axisLine: { stroke: '#d1d5db' },
    tickLine: { stroke: '#d1d5db' }
  };
  const pdfLegendProps = {
    iconType: 'circle',
    wrapperStyle: {
      fontSize: 11,
      color: '#374151',
      paddingTop: 4
    }
  };

  const renderDailyPdfLegend = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '18px',
        flexWrap: 'wrap',
        paddingTop: '6px',
        fontSize: '11px',
        color: '#374151'
      }}
    >
      {pdfReportSubjectKeys.map((subjectKey) => (
        <div
          key={`daily-legend-${subjectKey}`}
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '999px',
              backgroundColor: reportSubjectMeta[subjectKey]?.color || '#64748b',
              display: 'inline-block'
            }}
          />
          <span>{reportSubjectMeta[subjectKey]?.label || subjectKey}</span>
        </div>
      ))}
    </div>
  );

  const renderSmartPdfBarLabel = (insideColor = '#ffffff', outsideColor = '#111827', specificDataKey = null) => (props) => {
    const { x, y, width, height, value, index } = props;
    const num = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(num)) return null;

    // Check if the data point is absent — show "AB" above the bar position
    const dataItem = props?.payload;
    const key = specificDataKey || props.dataKey;
    const isAbsentEntry = (dataItem?.absent && num === 0 && key === 'student')
      || (key === 'value' && num === 0 && dataItem?.hasValue && !dataItem?.value_display?.match(/^[0-9]/))
      || (key === 'student_absent');

    const label = isAbsentEntry ? 'AB' : formatChartValue(num);
    const topY = Number(y);
    const centerX = Number(x) + Number(width) / 2;

    // For absent entries, always place label above; for normal bars use smart placement
    if (isAbsentEntry) {
      return (
        <text
          x={centerX}
          y={topY - 6}
          textAnchor="middle"
          fontSize={11}
          fontWeight={700}
          fill="#dc2626"
          stroke="#ffffff"
          strokeWidth={2}
          paintOrder="stroke"
        >
          {label}
        </text>
      );
    }

    // If there is visible space above the bar, place label outside; otherwise keep it inside.
    const hasSpaceAbove = Number.isFinite(topY) && topY > 16;
    const labelY = hasSpaceAbove ? topY - 6 : topY + Math.max(12, Math.min(Number(height) - 4, 14));

    return (
      <text
        x={centerX}
        y={labelY}
        textAnchor="middle"
        fontSize={11}
        fontWeight={700}
        fill={hasSpaceAbove ? outsideColor : insideColor}
        stroke={hasSpaceAbove ? '#ffffff' : 'none'}
        strokeWidth={hasSpaceAbove ? 2 : 0}
        paintOrder="stroke"
      >
        {label}
      </text>
    );
  };

  const exportStudentPdfReport = async (options = {}) => {
    const { download = true, silent = false, returnBlob = false } = options;
    const isBulkCaptureMode = autoGeneratePdf && returnBlob && !download;

    if (exportingPdf) {
      return null;
    }

    const root = reportExportRef.current;
    if (!root) {
      throw new Error('PDF export layout is not ready');
    }

    try {
      setExportingPdf(true);

      // Allow chart layers to finish rendering before capture.
      await new Promise((resolve) => setTimeout(resolve, 600));

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pages = Array.from(root.querySelectorAll('.student-pdf-page'));

      if (pages.length === 0) {
        throw new Error('PDF export layout contains no pages');
      }

      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        const canvas = await html2canvas(page, {
          scale: isBulkCaptureMode ? 1.35 : 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
        const renderWidth = imgWidth * ratio;
        const renderHeight = imgHeight * ratio;
        const x = (pageWidth - renderWidth) / 2;
        const y = (pageHeight - renderHeight) / 2;

        if (i > 0) pdf.addPage();
        if (isBulkCaptureMode) {
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.82), 'JPEG', x, y, renderWidth, renderHeight, undefined, 'FAST');
        } else {
          pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, renderWidth, renderHeight, undefined, 'FAST');
        }

        // Release canvas memory between pages in bulk mode.
        if (isBulkCaptureMode) {
          canvas.width = 1;
          canvas.height = 1;
          await new Promise((resolve) => setTimeout(resolve, 30));
        }
      }

      const fileName = `${displayData.name.replace(/\s+/g, '_')}_${displayData.rollNo}_Progress_Report.pdf`;
      const blob = (returnBlob || !download) ? pdf.output('blob') : null;

      if (download) {
        pdf.save(fileName);
      }

      return { blob, fileName };
    } catch (err) {
      console.error('Failed to generate PDF report:', err);
      if (!silent) {
        toast.error('Failed to generate PDF report. Please try again.');
      }
      if (isBulkCaptureMode) throw err;
      return null;
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    autoPdfTriggeredRef.current = false;
  }, [studentNo]);

  useEffect(() => {
    if (!autoGeneratePdf || autoPdfTriggeredRef.current || loading || exportingPdf) return;

    if (error) {
      autoPdfTriggeredRef.current = true;
      if (onBulkPdfError) onBulkPdfError(error);
      return;
    }

    autoPdfTriggeredRef.current = true;

    const runAutoExport = async () => {
      try {
        const result = await exportStudentPdfReport({ download: false, silent: true, returnBlob: true });
        if (result && result.blob) {
          if (onBulkPdfReady) onBulkPdfReady(result);
        } else if (onBulkPdfError) {
          onBulkPdfError('PDF export returned an empty result');
        }
      } catch (err) {
        if (onBulkPdfError) {
          onBulkPdfError(err.message || 'PDF generation failed');
        }
      }
    };

    runAutoExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGeneratePdf, loading, exportingPdf, studentNo]);

  const metricInfo = {
    overallAverage: 'This is your average performance across all available tests. Higher is better.',
    batchPercentile: 'This shows where you stand in your batch. 70% means you scored better than about 70 out of 100 classmates.',
    participationRate: 'This is how many tests you attended compared to tests conducted for your batch. 100% means you attended all.',
    consistency: 'This shows how steady your marks are. Lower value = more consistent performance; higher value = marks are fluctuating more.',
    trendSlope: 'This shows whether your performance is improving or dropping over time. Positive = improving, negative = declining.',
    riskLevel: 'This is an overall alert level based on score, trend, participation, and absences/non-numeric marks. Low is good, High needs attention.'
  };

  const renderInsightMetric = (metric = {}) => {
    if (metric.delta !== undefined && metric.delta !== null) {
      const val = Number(metric.delta);
      const sign = val > 0 ? '+' : '';
      return `${sign}${val}`;
    }
    if (metric.rank !== undefined && metric.total !== undefined) {
      return `#${metric.rank}/${metric.total}`;
    }
    if (metric.subjects && Array.isArray(metric.subjects)) {
      return metric.subjects.join(', ');
    }
    if (metric.delta_vs_avg !== undefined && metric.delta_vs_avg !== null) {
      const val = Number(metric.delta_vs_avg);
      const sign = val > 0 ? '+' : '';
      return `${sign}${val} vs avg`;
    }
    return null;
  };

  const renderPdfScoreSummary = (testInsight) => {
    const parts = [];
    if (testInsight?.score !== null && testInsight?.score !== undefined) {
      const scoreVal = Number(testInsight.score);
      if (!Number.isNaN(scoreVal)) parts.push(`Score: ${Math.round(scoreVal)}`);
    }
    if (testInsight?.class_avg !== null && testInsight?.class_avg !== undefined) {
      const avgVal = Number(testInsight.class_avg);
      if (!Number.isNaN(avgVal)) parts.push(`Class Avg: ${Math.round(avgVal)}`);
    }
    if (testInsight?.rank && testInsight?.rank_total) {
      parts.push(`Rank: #${testInsight.rank}/${testInsight.rank_total}`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'No score summary available';
  };

  const renderMetricLabel = (label, infoKey) => (
    <span className="metric-label-with-help">
      {label}
      <span
        className="metric-help-icon"
        title={metricInfo[infoKey]}
        aria-label={metricInfo[infoKey]}
      >
        ⓘ
      </span>
    </span>
  );

  // Save feedback to backend API
  const handleSaveFeedback = async () => {
    if (!studentId) return;
    if (!currentFeedback.teacherFeedback && !currentFeedback.suggestions) {
      toast.warning('Please enter feedback or suggestions');
      return;
    }
    try {
      setSavingFeedback(true);
      const response = await authFetch(`${API_BASE}/api/analysis/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentId,
          feedback_date: currentFeedback.date,
          teacher_feedback: currentFeedback.teacherFeedback,
          suggestions: currentFeedback.suggestions,
          academic_director_signature: currentFeedback.academicDirectorSignature,
          student_signature: currentFeedback.studentSignature,
          parent_signature: currentFeedback.parentSignature
        })
      });
      if (response.ok) {
        toast.success('Feedback saved successfully!');
        setCurrentFeedback({
          date: new Date().toISOString().split('T')[0],
          teacherFeedback: '',
          suggestions: '',
          academicDirectorSignature: '',
          studentSignature: '',
          parentSignature: ''
        });
        // Refresh only feedback list from DB
        await fetchFeedback();
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to save feedback');
      }
    } catch (err) {
      toast.error('Error saving feedback: ' + err.message);
    } finally {
      setSavingFeedback(false);
    }
  };

  // Generate Excel report for the student
  const generateExcelReport = () => {
    const wb = XLSX.utils.book_new();

    // ===== Sheet 1: Personal Information =====
    const personalRows = [
      ['STUDENT REPORT'],
      ['Generated on', new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })],
      [],
      ['PERSONAL INFORMATION'],
      ['Admission Number', displayData.rollNo],
      ['Student Name', displayData.name],
      ['Date of Birth', displayData.dob],
      ['Gender', displayData.gender],
      ['Grade', displayData.grade],
      ['Community', displayData.community],
      ['Academic Year', displayData.academicYear],
      ['Program', reportProgram],
      ['Board', displayData.board],
      ['Student Mobile', displayData.studentMobile],
      ['Aadhar Number', displayData.aadharNumber],
      ['APAAR ID', displayData.aasarId],
      ['Email', displayData.emailId],
      ['School Name', displayData.schoolName],
      [],
      ['FAMILY DETAILS'],
      ['Guardian Name', displayData.guardianName],
      ['Guardian Occupation', displayData.guardianOccupation],
      ['Guardian Contact', displayData.guardianContact],
      ['Guardian Email', displayData.guardianEmail],
      ['Father Name', displayData.fatherName],
      ['Father Occupation', displayData.fatherOccupation],
      ['Father Contact', displayData.fatherContact],
      ['Father Email', displayData.fatherEmail],
      ['Mother Name', displayData.motherName],
      ['Mother Occupation', displayData.motherOccupation],
      ['Mother Contact', displayData.motherContact],
      ['Mother Email', displayData.motherEmail],
      ['Sibling Name', displayData.siblingName],
      ['Sibling Grade', displayData.siblingGrade],
      ['Sibling School', displayData.siblingSchool],
      ['Sibling College', displayData.siblingCollege],
      [],
      ['COUNSELLING DETAILS'],
      ['Forum 1', displayData.counselling1.forum],
      ['Round 1', displayData.counselling1.round],
      ['All India Rank 1', displayData.counselling1.allIndiaRank],
      ['Community Rank 1', displayData.counselling1.communityRank],
      ['College Allotted 1', displayData.counselling1.collegeAlloted],
      [],
      ['Forum 2', displayData.counselling2.forum],
      ['Round 2', displayData.counselling2.round],
      ['All India Rank 2', displayData.counselling2.allIndiaRank],
      ['Community Rank 2', displayData.counselling2.communityRank],
      ['College Allotted 2', displayData.counselling2.collegeAlloted],
      [],
      ['Forum 3', displayData.counselling3.forum],
      ['Round 3', displayData.counselling3.round],
      ['All India Rank 3', displayData.counselling3.allIndiaRank],
      ['Community Rank 3', displayData.counselling3.communityRank],
      ['College Allotted 3', displayData.counselling3.collegeAlloted],
    ];
    const wsPersonal = XLSX.utils.aoa_to_sheet(personalRows);
    wsPersonal['!cols'] = [{ wch: 22 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsPersonal, 'Personal Info');

    // ===== Sheet 2: Academic Marks (10th, 12th, Entrance) =====
    const academicRows = [
      ['ACADEMIC MARKS'],
      [],
      ['10TH STANDARD'],
      ['School Name', displayData.std10Marks.schoolName],
      ['Year of Passing', displayData.std10Marks.yearOfPassing],
      ['Board of Study', displayData.std10Marks.boardOfStudy],
      [],
      ['Subject', 'Marks'],
      ['English', displayData.std10Marks.english],
      ['Tamil', displayData.std10Marks.tamil],
      ['Hindi', displayData.std10Marks.hindi],
      ['Mathematics', displayData.std10Marks.maths],
      ['Science', displayData.std10Marks.science],
      ['Social Science', displayData.std10Marks.socialScience],
      ['Total', displayData.std10Marks.total],
      [],
      ['12TH STANDARD'],
      ['School Name', displayData.std12Marks.schoolName],
      ['Year of Passing', displayData.std12Marks.yearOfPassing],
      ['Board of Study', displayData.std12Marks.boardOfStudy],
      [],
      ['Subject', 'Marks'],
      ['English', displayData.std12Marks.english],
      ['Tamil', displayData.std12Marks.tamil],
      ['Physics', displayData.std12Marks.physics],
      ['Chemistry', displayData.std12Marks.chemistry],
      ['Mathematics', displayData.std12Marks.mathematics],
      ['Biology', displayData.std12Marks.biology],
      ['Computer Science', displayData.std12Marks.computerScience],
      ['Total', displayData.std12Marks.total],
    ];

    academicRows.push([], ['ENTRANCE EXAMS']);
    academicRows.push(['Exam Name', 'Percentile', 'Mark']);
    if (displayData.entrance_exam_1) academicRows.push([displayData.entrance_exam_1, displayData.entrance_exam_1_percentile, displayData.entrance_exam_1_mark]);
    if (displayData.entrance_exam_2) academicRows.push([displayData.entrance_exam_2, displayData.entrance_exam_2_percentile, displayData.entrance_exam_2_mark]);
    if (displayData.entrance_exam_3) academicRows.push([displayData.entrance_exam_3, displayData.entrance_exam_3_percentile, displayData.entrance_exam_3_mark]);


    const wsAcademic = XLSX.utils.aoa_to_sheet(academicRows);
    wsAcademic['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsAcademic, 'Academic Marks');

    // ===== Sheet 3: Unit Test Performance =====
    if (dailyTests.length > 0) {
      const dailyHeader = ['Date', 'Subject', 'Unit Name', 'Marks (Obtained/Total)', 'Class Avg', 'Top Score'];
      const dailyRows = dailyTests.map(test => [
        test.test_date ? new Date(test.test_date).toLocaleDateString('en-IN') : '-',
        test.subject || '-',
        test.unit_name || '-',
        displayMarkWithTotal(test.marks, test.subject_total_marks ?? test.test_total_marks, 0),
        displayMarkWithTotal(test.class_avg, test.subject_total_marks ?? test.test_total_marks, 0),
        displayMarkWithTotal(test.top_score, test.subject_total_marks ?? test.test_total_marks, 0)
      ]);
      const wsDaily = XLSX.utils.aoa_to_sheet([['UNIT TEST PERFORMANCE'], [], dailyHeader, ...dailyRows]);
      wsDaily['!cols'] = [{ wch: 14 }, { wch: 16 }, { wch: 28 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsDaily, 'Unit Tests');
    }

    // ===== Sheet 4: Monthly Test Performance =====
    if (mockTests.length > 0) {
      const mockHeader = ['Date', 'Maths (Obtained/Total)', 'Physics (Obtained/Total)', 'Chemistry (Obtained/Total)', 'Biology (Obtained/Total)', 'Total (Obtained/Total)', 'Class Avg', 'Top Score'];
      const mockRows = mockTests.map(test => [
        test.test_date ? new Date(test.test_date).toLocaleDateString('en-IN') : '-',
        displayMarkWithTotal(test.maths_marks, test.maths_total_marks, 0),
        displayMarkWithTotal(test.physics_marks, test.physics_total_marks, 0),
        displayMarkWithTotal(test.chemistry_marks, test.chemistry_total_marks, 0),
        displayMarkWithTotal(test.biology_marks, test.biology_total_marks, 0),
        displayMarkWithTotal(test.total_marks, test.test_total_marks, 0),
        displayMarkWithTotal(test.class_avg_total, test.test_total_marks, 0),
        displayMarkWithTotal(test.top_score_total, test.test_total_marks, 0)
      ]);
      const wsMock = XLSX.utils.aoa_to_sheet([['MONTHLY TEST PERFORMANCE'], [], mockHeader, ...mockRows]);
      wsMock['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsMock, 'Monthly Tests');
    }

    // ===== Sheet 5: Feedback History =====
    if (feedbackList.length > 0) {
      const fbHeader = ['Date', 'Teacher Feedback', 'Suggestions', 'Academic Director', 'Student', 'Parent'];
      const fbRows = feedbackList.map(fb => [
        (fb.feedback_date || fb.date) ? new Date(fb.feedback_date || fb.date).toLocaleDateString('en-IN') : '-',
        fb.teacher_feedback || '-',
        fb.suggestions || '-',
        fb.academic_director_signature || '-',
        fb.student_signature || '-',
        fb.parent_signature || '-'
      ]);
      const wsFeedback = XLSX.utils.aoa_to_sheet([['FEEDBACK HISTORY'], [], fbHeader, ...fbRows]);
      wsFeedback['!cols'] = [{ wch: 14 }, { wch: 40 }, { wch: 40 }, { wch: 20 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, wsFeedback, 'Feedback');
    }

    // Generate and download
    const fileName = `${displayData.name.replace(/\s+/g, '_')}_${displayData.rollNo}_Report.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Trigger PDF export once filtered data has recomputed after date selection
  useEffect(() => {
    if (!pdfExportPending) return;
    setPdfExportPending(false);
    // Brief delay allows Recharts to re-render with updated filtered data
    // before html2canvas captures the DOM
    const rafId = requestAnimationFrame(() => {
      setTimeout(() => exportStudentPdfReport(), 200);
    });
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfExportPending]);

  // Show loading state
  if (loading) {
    return (
      <div className="student-profile">
        <div className="profile-header">
          <button className="back-button" onClick={onBack}>← Back to Students</button>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#5b5fc7' }}>
          Loading student data...
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !studentData) {
    return (
      <div className="student-profile">
        <div className="profile-header">
          <button className="back-button" onClick={onBack}>← Back to Students</button>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ color: '#c00', marginBottom: '20px' }}>
            <strong>Error:</strong> {error || 'Student data not found'}
          </div>
          <button onClick={fetchStudentData} style={{ padding: '10px 20px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleDateFilterConfirm = ({ dateFrom, dateTo }) => {
    setShowDateFilterModal(false);
    setPdfDateFrom(dateFrom);
    setPdfDateTo(dateTo);
    setPdfExportPending(true);
  };

  const formatDateRangeLabel = () => {
    if (!effectivePdfDateFrom && !effectivePdfDateTo) return null;
    const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
    if (effectivePdfDateFrom && effectivePdfDateTo) return `${fmt(effectivePdfDateFrom)} – ${fmt(effectivePdfDateTo)}`;
    if (effectivePdfDateFrom) return `From ${fmt(effectivePdfDateFrom)}`;
    return `Until ${fmt(effectivePdfDateTo)}`;
  };

  return (
    <div className="student-profile">
      <div className="profile-header">
        <div className="profile-header-actions">
          <button className="back-button" onClick={onBack}>← Back to Students</button>
          <button className="btn-download-report" onClick={() => setShowDateFilterModal(true)} disabled={exportingPdf}>
            {exportingPdf ? '⏳ Generating PDF...' : '📄 Download Progress PDF'}
          </button>
          <button className="btn-download-report" onClick={generateExcelReport}>
            📊 Download Report
          </button>
        </div>

      {showDateFilterModal && (
        <DateFilterModal
          title="Select Date Range for Progress Report"
          onConfirm={handleDateFilterConfirm}
          onCancel={() => setShowDateFilterModal(false)}
        />
      )}
        <div className="profile-title-section">
          <h2>Student Profile - {displayData.name}</h2>
          <div className="student-photo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <div style={{ position: 'relative', width: '150px', height: '150px' }}>
              <img 
                src={studentData?.photo_url ? `${API_BASE}${studentData.photo_url}` : DEFAULT_AVATAR} 
                alt={studentData?.student_name} 
                style={{ objectFit: 'cover', width: '150px', height: '150px', borderRadius: '50%' }}
              />
              {studentData?.photo_url && (
                <button
                  onClick={handlePhotoDelete}
                  disabled={deletingPhoto}
                  title={deletingPhoto ? 'Deleting...' : 'Delete Photo'}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: '#fee2e2',
                    color: '#dc2626',
                    border: '1px solid #fca5a5',
                    fontSize: '14px',
                    cursor: deletingPhoto ? 'not-allowed' : 'pointer',
                    opacity: deletingPhoto ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    lineHeight: 1,
                    zIndex: 1
                  }}
                >
                  {deletingPhoto ? '⏳' : '🗑️'}
                </button>
              )}
            </div>
            <label className={`btn-upload-photo ${uploadingPhoto ? 'disabled' : ''}`} style={{ 
              cursor: 'pointer', 
              padding: '6px 12px', 
              backgroundColor: '#f1f5f9', 
              border: '1px solid #cbd5e1', 
              borderRadius: '6px',
              fontSize: '13px',
              display: 'inline-block'
            }}>
              {uploadingPhoto ? 'Uploading...' : '📷 Upload Photo'}
              <input 
                type="file" 
                accept=".jpg,.jpeg,.png,.gif,.webp" 
                style={{ display: 'none' }} 
                onChange={handlePhotoUpload}
                disabled={uploadingPhoto}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`}
          onClick={() => setActiveTab('personal')}
        >
          Personal Information
        </button>
        <button
          className={`tab-button ${activeTab === 'marks' ? 'active' : ''}`}
          onClick={() => setActiveTab('marks')}
        >
          Marks & Analysis
        </button>
        <button
          className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback & Suggestions
        </button>
      </div>

      {/* Personal Information Tab */}
      {activeTab === 'personal' && (
        <div className="tab-content">
          {/* Basic Student Details Section */}
          <div className="profile-section">
            <h3>Personal Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Admission Number:</label>
                <span>{displayData.rollNo}</span>
              </div>
              <div className="detail-item">
                <label>Student Name:</label>
                <span>{displayData.name}</span>
              </div>
              <div className="detail-item">
                <label>Date of Birth:</label>
                <span>{displayData.dob}</span>
              </div>
              <div className="detail-item">
                <label>Grade:</label>
                <span>{displayData.grade}</span>
              </div>
              <div className="detail-item">
                <label>Community:</label>
                <span>{displayData.community}</span>
              </div>
              <div className="detail-item">
                <label>Academic Year:</label>
                <span>{displayData.academicYear}</span>
              </div>
              <div className="detail-item">
                <label>Course:</label>
                <span>{displayData.course}</span>
              </div>
              <div className="detail-item">
                <label>Board:</label>
                <span>{displayData.board}</span>
              </div>
              <div className="detail-item">
                <label>Student Mobile:</label>
                <span>{displayData.studentMobile}</span>
              </div>
              <div className="detail-item">
                <label>Aadhar Number:</label>
                <span>{displayData.aadharNumber}</span>
              </div>
              <div className="detail-item">
                <label>APAAR ID:</label>
                <span>{displayData.aasarId}</span>
              </div>
              <div className="detail-item">
                <label>Email ID:</label>
                <span>{displayData.emailId}</span>
              </div>
            </div>
          </div>

          {/* School and Family Details */}
          <div className="profile-section">
            <h3>School & Family Details</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>School Name:</label>
                <span>{displayData.schoolName}</span>
              </div>
              <div className="detail-item">
                <label>Guardian Name:</label>
                <span>{displayData.guardianName}</span>
              </div>
              <div className="detail-item">
                <label>Guardian Occupation:</label>
                <span>{displayData.guardianOccupation}</span>
              </div>
              <div className="detail-item">
                <label>Guardian Contact:</label>
                <span>{displayData.guardianContact}</span>
              </div>
              <div className="detail-item">
                <label>Guardian Email:</label>
                <span>{displayData.guardianEmail}</span>
              </div>
              <div className="detail-item">
                <label>Father Name:</label>
                <span>{displayData.fatherName}</span>
              </div>
              <div className="detail-item">
                <label>Father Occupation:</label>
                <span>{displayData.fatherOccupation}</span>
              </div>
              <div className="detail-item">
                <label>Father Contact:</label>
                <span>{displayData.fatherContact}</span>
              </div>
              <div className="detail-item">
                <label>Father Email:</label>
                <span>{displayData.fatherEmail}</span>
              </div>
              <div className="detail-item">
                <label>Mother Name:</label>
                <span>{displayData.motherName}</span>
              </div>
              <div className="detail-item">
                <label>Mother Occupation:</label>
                <span>{displayData.motherOccupation}</span>
              </div>
              <div className="detail-item">
                <label>Mother Contact:</label>
                <span>{displayData.motherContact}</span>
              </div>
              <div className="detail-item">
                <label>Mother Email:</label>
                <span>{displayData.motherEmail}</span>
              </div>
              <div className="detail-item">
                <label>Sibling Name:</label>
                <span>{displayData.siblingName}</span>
              </div>
              <div className="detail-item">
                <label>Sibling Grade:</label>
                <span>{displayData.siblingGrade}</span>
              </div>
              <div className="detail-item">
                <label>Sibling School:</label>
                <span>{displayData.siblingSchool}</span>
              </div>
              <div className="detail-item">
                <label>Sibling College:</label>
                <span>{displayData.siblingCollege}</span>
              </div>
            </div>
          </div>

          {/* 10th Standard Marks */}
          <div className="profile-section">
            <h3>10th Standard Marks</h3>
            <div className="details-grid" style={{ marginBottom: '15px' }}>
              <div className="detail-item">
                <label>School Name:</label>
                <span>{displayData.std10Marks.schoolName}</span>
              </div>
              <div className="detail-item">
                <label>Year of Passing:</label>
                <span>{displayData.std10Marks.yearOfPassing}</span>
              </div>
              <div className="detail-item">
                <label>Board of Study:</label>
                <span>{displayData.std10Marks.boardOfStudy}</span>
              </div>
            </div>
            <div className="marks-table">
              <table>
                <thead>
                  <tr>
                    <th>English</th>
                    <th>Tamil</th>
                    <th>Hindi</th>
                    <th>Mathematics</th>
                    <th>Science</th>
                    <th>Social Science</th>
                    <th>Total Marks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{displayData.std10Marks.english}</td>
                    <td>{displayData.std10Marks.tamil}</td>
                    <td>{displayData.std10Marks.hindi}</td>
                    <td>{displayData.std10Marks.maths}</td>
                    <td>{displayData.std10Marks.science}</td>
                    <td>{displayData.std10Marks.socialScience}</td>
                    <td><strong>{displayData.std10Marks.total}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 12th Standard Marks */}
          <div className="profile-section">
            <h3>12th Standard Marks</h3>
            <div className="details-grid" style={{ marginBottom: '15px' }}>
              <div className="detail-item">
                <label>School Name:</label>
                <span>{displayData.std12Marks.schoolName}</span>
              </div>
              <div className="detail-item">
                <label>Year of Passing:</label>
                <span>{displayData.std12Marks.yearOfPassing}</span>
              </div>
              <div className="detail-item">
                <label>Board of Study:</label>
                <span>{displayData.std12Marks.boardOfStudy}</span>
              </div>
            </div>
            <div className="marks-table">
              <table>
                <thead>
                  <tr>
                    <th>English</th>
                    <th>Tamil</th>
                    <th>Physics</th>
                    <th>Chemistry</th>
                    <th>Mathematics</th>
                    <th>Biology</th>
                    <th>Computer Science</th>
                    <th>Total Marks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{displayData.std12Marks.english}</td>
                    <td>{displayData.std12Marks.tamil}</td>
                    <td>{displayData.std12Marks.physics}</td>
                    <td>{displayData.std12Marks.chemistry}</td>
                    <td>{displayData.std12Marks.mathematics}</td>
                    <td>{displayData.std12Marks.biology}</td>
                    <td>{displayData.std12Marks.computerScience}</td>
                    <td><strong>{displayData.std12Marks.total}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Entrance Exam Marks */}
          <div className="profile-section">
            <h3>Entrance Exam Marks</h3>
            {(displayData.entrance_exam_1 || displayData.entrance_exam_2 || displayData.entrance_exam_3) ? (
              <div className="marks-table">
                <table>
                  <thead>
                    <tr>
                      <th>Exam Name</th>
                      <th>Percentile</th>
                      <th>Mark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map(num => {
                      const name = displayData[`entrance_exam_${num}`];
                      const percentile = displayData[`entrance_exam_${num}_percentile`];
                      const mark = displayData[`entrance_exam_${num}_mark`];
                      if (!name) return null;
                      return (
                        <tr key={num}>
                          <td className="exam-name">{name}</td>
                          <td>{displayMark(percentile)}</td>
                          <td>{displayMark(mark)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                No entrance exam data available
              </p>
            )}
          </div>

          {/* Counselling Details */}
          <div className="profile-section">
            <h3>Counselling Details</h3>
            {[1, 2, 3].map(num => {
              const c = displayData[`counselling${num}`];
              if (!c || c.forum === 'N/A') return null;
              return (
                <div key={num} style={{ marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '8px', color: '#5b5fc7' }}>Forum {num}</h4>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Forum of Counselling:</label>
                      <span>{c.forum}</span>
                    </div>
                    <div className="detail-item">
                      <label>Round:</label>
                      <span>{c.round}</span>
                    </div>
                    <div className="detail-item">
                      <label>All India Rank:</label>
                      <span>{c.allIndiaRank}</span>
                    </div>
                    <div className="detail-item">
                      <label>Community Rank:</label>
                      <span>{c.communityRank}</span>
                    </div>
                    <div className="detail-item">
                      <label>College Allotted:</label>
                      <span>{c.collegeAlloted}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {displayData.counselling1.forum === 'N/A' && displayData.counselling2.forum === 'N/A' && displayData.counselling3.forum === 'N/A' && (
              <p style={{ color: '#666', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                No counselling data available
              </p>
            )}
          </div>
        </div>
      )}

      {/* Marks & Analysis Tab */}
      {activeTab === 'marks' && (
        <div className="tab-content">
          <div className="profile-section">
            <h3>📌 Advanced Performance Metrics</h3>
            {metricsLoading ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>Loading advanced metrics...</p>
            ) : studentMetrics ? (
              <>
                <div className="student-metrics-grid">
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Overall Average', 'overallAverage')}</label>
                    <p>{studentMetrics.overall_avg_pct}%</p>
                  </div>
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Batch Percentile', 'batchPercentile')}</label>
                    <p>{studentMetrics.percentile_overall}%</p>
                  </div>
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Participation Rate', 'participationRate')}</label>
                    <p>{studentMetrics.participation_rate}%</p>
                  </div>
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Consistency (Std Dev)', 'consistency')}</label>
                    <p>{studentMetrics.consistency_stddev}</p>
                  </div>
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Trend Slope', 'trendSlope')}</label>
                    <p>{studentMetrics.trend_slope}</p>
                  </div>
                  <div className="student-metric-card">
                    <label>{renderMetricLabel('Risk Level', 'riskLevel')}</label>
                    <p className={`risk-pill ${studentMetrics.risk_level || 'low'}`}>
                      {(studentMetrics.risk_level || 'low').toUpperCase()} ({studentMetrics.risk_score})
                    </p>
                  </div>
                </div>
                {studentMetrics.reasons?.length > 0 && (
                  <div className="student-risk-reasons">
                    <h4>Risk Reasons</h4>
                    <ul>
                      {studentMetrics.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                    <p><strong>Recommended Action:</strong> {studentMetrics.recommended_action}</p>
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic' }}>Advanced metrics unavailable for this student.</p>
            )}
          </div>

          <div className="profile-section">
            <h3>🧩 Per-Test Remediation Insights</h3>
            <div className="insight-tab-row">
              <button className={`insight-tab-btn ${insightTab === 'mock' ? 'active' : ''}`} onClick={() => setInsightTab('mock')}>
                Monthly Test Insights
              </button>
              <button className={`insight-tab-btn ${insightTab === 'daily' ? 'active' : ''}`} onClick={() => setInsightTab('daily')}>
                Unit Test Insights
              </button>
            </div>

            {insightsLoading ? (
              <p style={{ color: '#666', fontStyle: 'italic' }}>Loading per-test remediation insights...</p>
            ) : activeInsightRows.length > 0 ? (
              <div className="insight-lane-grid">
                {activeInsightRows.map((testInsight) => (
                  <div className="insight-test-column" key={`${testInsight.test_type}-${testInsight.test_id}`}>
                    <div className="insight-test-title">
                      <strong>{testInsight.test_label}</strong>
                      <span>{testInsight.test_date ? new Date(testInsight.test_date).toLocaleDateString('en-IN') : 'N/A'}</span>
                    </div>

                    <div className="insight-section-title achievement">● ACHIEVEMENTS ({(testInsight.achievements || []).length})</div>
                    {(testInsight.achievements || []).length > 0 ? (
                      (testInsight.achievements || []).map((item, idx) => (
                        <div className="insight-item achievement" key={`ach-${testInsight.test_id}-${idx}`}>
                          <div className="insight-item-title">{item.title}</div>
                          <div className="insight-item-detail">{item.detail}</div>
                          {renderInsightMetric(item.metric) && <div className="insight-item-metric">{renderInsightMetric(item.metric)}</div>}
                        </div>
                      ))
                    ) : (
                      <div className="insight-empty">No achievements for this test.</div>
                    )}

                    <div className="insight-section-title redflag">● RED FLAGS ({(testInsight.red_flags || []).length})</div>
                    {(testInsight.red_flags || []).length > 0 ? (
                      (testInsight.red_flags || []).map((item, idx) => (
                        <div className="insight-item redflag" key={`red-${testInsight.test_id}-${idx}`}>
                          <div className="insight-item-title">{item.title}</div>
                          <div className="insight-item-detail">{item.detail}</div>
                          {renderInsightMetric(item.metric) && <div className="insight-item-metric">{renderInsightMetric(item.metric)}</div>}
                          <div className="insight-remedy">Plan: {item.remediation_action}</div>
                        </div>
                      ))
                    ) : (
                      <div className="insight-empty">No red flags for this test.</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic' }}>
                No per-test remediation insights available.
              </p>
            )}
          </div>

          {/* Unit Test Performance */}
          <div className="profile-section">
            <h3>📚 Unit Test Performance</h3>
            <div className="date-filter-row">
              <div className="date-filter-field">
                <label>From</label>
                <input type="date" value={dailyDateFrom} onChange={e => setDailyDateFrom(e.target.value)} className="date-filter-input" />
              </div>
              <div className="date-filter-field">
                <label>To</label>
                <input type="date" value={dailyDateTo} onChange={e => setDailyDateTo(e.target.value)} className="date-filter-input" />
              </div>
              {(dailyDateFrom || dailyDateTo) && (
                <button className="date-filter-clear" onClick={() => { setDailyDateFrom(''); setDailyDateTo(''); }}>✕ Clear</button>
              )}
            </div>
            {filteredDailyTests.length > 0 ? (
              <>
                <div className="marks-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Subject</th>
                        <th>Unit Covered</th>
                        <th>Marks (Obtained/Total)</th>
                        <th>Class Avg</th>
                        <th>Top Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDailyTests.map((test, index) => (
                        <tr key={test.test_id || index}>
                          <td>{test.test_date ? new Date(test.test_date).toLocaleDateString('en-IN') : 'N/A'}</td>
                          <td className="exam-name">{test.subject || 'N/A'}</td>
                          <td>{test.unit_name || 'N/A'}</td>
                          <td><strong>{displayMarkWithTotal(test.marks, test.subject_total_marks ?? test.test_total_marks, 0)}</strong></td>
                          <td>{displayMarkWithTotal(test.class_avg, test.subject_total_marks ?? test.test_total_marks, 0)}</td>
                          <td className="top-score">{displayMarkWithTotal(test.top_score, test.subject_total_marks ?? test.test_total_marks, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Performance Trend Chart */}
                {performanceTrend.length > 1 && (
                  <div className="profile-section" style={{ marginTop: '20px' }}>
                    <div className="chart-control-row">
                      <h4>Performance Trend</h4>
                      <div className="date-filter-field">
                        <label>Chart Type</label>
                        <select className="date-filter-input" value={dailyChartType} onChange={(e) => setDailyChartType(e.target.value)}>
                          <option value="line">Line Chart</option>
                          <option value="area">Area Chart</option>
                          <option value="bar">Bar Chart</option>
                        </select>
                      </div>
                    </div>
                    {dailyChartType === 'line' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={performanceTrend} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" {...chartAxisStyle} />
                          <YAxis {...chartAxisStyle} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(value, name, payload) => {
                              const unit = payload?.payload?.unit || 'marks';
                              if (payload?.payload?.absent && name === 'Your Score') return ['Absent', name];
                              return [unit === '%' ? `${value}%` : value, name];
                            }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="score" stroke="#5b5fc7" strokeWidth={3} name="Your Score" dot={{ r: 5 }} />
                          <Line type="monotone" dataKey="classAvg" stroke="#a0aec0" strokeWidth={2} strokeDasharray="5 5" name="Class Average" dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                    {dailyChartType === 'area' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={performanceTrend} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" {...chartAxisStyle} />
                          <YAxis {...chartAxisStyle} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(value, name, payload) => {
                              const unit = payload?.payload?.unit || 'marks';
                              if (payload?.payload?.absent && name === 'Your Score') return ['Absent', name];
                              return [unit === '%' ? `${value}%` : value, name];
                            }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="score" stroke="#5b5fc7" fill="#5b5fc733" strokeWidth={2} name="Your Score" />
                          <Area type="monotone" dataKey="classAvg" stroke="#a0aec0" fill="#a0aec033" strokeWidth={2} name="Class Average" />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                    {dailyChartType === 'bar' && (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={performanceTrend} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" {...chartAxisStyle} />
                          <YAxis {...chartAxisStyle} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(value, name, payload) => {
                              const unit = payload?.payload?.unit || 'marks';
                              if (payload?.payload?.absent && name === 'Your Score') return ['Absent', name];
                              return [unit === '%' ? `${value}%` : value, name];
                            }}
                          />
                          <Legend />
                          <Bar dataKey="score" fill="#5b5fc7" name="Your Score" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="classAvg" fill="#a0aec0" name="Class Average" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                No unit test data available
              </p>
            )}
          </div>

          {/* Monthly Test Performance */}
          <div className="profile-section">
            <h3>🎯 Monthly Test Performance</h3>
            <div className="date-filter-row">
              <div className="date-filter-field">
                <label>From</label>
                <input type="date" value={mockDateFrom} onChange={e => setMockDateFrom(e.target.value)} className="date-filter-input" />
              </div>
              <div className="date-filter-field">
                <label>To</label>
                <input type="date" value={mockDateTo} onChange={e => setMockDateTo(e.target.value)} className="date-filter-input" />
              </div>
              {(mockDateFrom || mockDateTo) && (
                <button className="date-filter-clear" onClick={() => { setMockDateFrom(''); setMockDateTo(''); }}>✕ Clear</button>
              )}
            </div>
            {filteredMockTests.length > 0 ? (
              <>
                <div className="chart-control-row" style={{ marginBottom: '12px' }}>
                  <h4 style={{ margin: 0 }}>Monthly Test Chart</h4>
                  <div className="date-filter-field">
                    <label>Chart Type</label>
                    <select className="date-filter-input" value={mockChartType} onChange={(e) => setMockChartType(e.target.value)}>
                      <option value="grouped">Grouped Bar (Subject-wise)</option>
                      <option value="trend">Line Trend (Total vs Class)</option>
                      <option value="radar">Radar (Latest Monthly Subject Profile)</option>
                      <option value="pie">Pie (Latest Monthly Subject Share)</option>
                    </select>
                  </div>
                </div>

                {mockChartType === 'grouped' && (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={mockTestChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="exam" {...chartAxisStyle} angle={-20} textAnchor="end" height={70} />
                      <YAxis {...chartAxisStyle} />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value, name, payload) => {
                          const unit = payload?.payload?.unit || 'marks';
                          if (payload?.payload?.absent) return ['Absent', name];
                          return [unit === '%' ? `${value}%` : value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="physics" fill="#FF6B9D" name="Physics" />
                      <Bar dataKey="chemistry" fill="#4A90E2" name="Chemistry" />
                      <Bar dataKey="biology" fill="#00D9C0" name="Biology" />
                      <Bar dataKey="maths" fill="#f1ed08" name="Maths" />
                    </BarChart>
                  </ResponsiveContainer>
                )}

                {mockChartType === 'trend' && (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={mockTrendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="exam" {...chartAxisStyle} />
                      <YAxis {...chartAxisStyle} />
                      <Tooltip
                        {...chartTooltipStyle}
                        formatter={(value, name, payload) => {
                          const unit = payload?.payload?.unit || 'marks';
                          if (payload?.payload?.absent && name === 'Your Total') return ['Absent', name];
                          return [unit === '%' ? `${value}%` : value, name];
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#5b5fc7" strokeWidth={3} name="Your Total" dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="classAvg" stroke="#38b2ac" strokeWidth={2} name="Class Average" dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="topScore" stroke="#48bb78" strokeWidth={2} name="Top Score" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}

                {mockChartType === 'radar' && latestMockSubjectShare.length > 0 && (
                  <ResponsiveContainer width="100%" height={350}>
                    <RadarChart data={latestMockSubjectShare}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="name" tick={{ fill: '#2d3748', fontSize: 12 }} />
                      <PolarRadiusAxis />
                      <Tooltip {...chartTooltipStyle} formatter={(value) => [`${value}`, 'Score']} />
                      <Radar dataKey="value" name="Marks" stroke="#5b5fc7" fill="#5b5fc7" fillOpacity={0.35} />
                    </RadarChart>
                  </ResponsiveContainer>
                )}

                {mockChartType === 'pie' && latestMockSubjectShare.length > 0 && (
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Tooltip {...chartTooltipStyle} formatter={(value) => [`${value}`, 'Score']} />
                      <Legend />
                      <Pie
                        data={latestMockSubjectShare}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        label
                      >
                        {latestMockSubjectShare.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                )}

                {(mockChartType === 'radar' || mockChartType === 'pie') && latestMockSubjectShare.length === 0 && (
                  <p style={{ color: '#666', fontStyle: 'italic', padding: '12px 0', textAlign: 'center' }}>
                    Not enough numeric marks in the latest monthly test for this chart.
                  </p>
                )}

                {/* Monthly Test Details Table */}
                <div className="marks-table" style={{ marginTop: '20px' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Subject Topics</th>
                        <th>Maths</th>
                        <th>Physics</th>
                        <th>Chemistry</th>
                        <th>Biology</th>
                        <th>Total (Obtained/Total)</th>
                        <th>Class Avg</th>
                        <th>Top Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMockTests.map((exam, index) => (
                        <tr key={exam.test_id || index}>
                          <td>{exam.test_date ? new Date(exam.test_date).toLocaleDateString('en-IN') : 'N/A'}</td>
                          <td>{formatMockTopics(exam)}</td>
                          <td>{displayMarkWithTotal(exam.maths_marks, exam.maths_total_marks, 0)}</td>
                          <td>{displayMarkWithTotal(exam.physics_marks, exam.physics_total_marks, 0)}</td>
                          <td>{displayMarkWithTotal(exam.chemistry_marks, exam.chemistry_total_marks, 0)}</td>
                          <td>{displayMarkWithTotal(exam.biology_marks, exam.biology_total_marks, 0)}</td>
                          <td><strong>{displayMarkWithTotal(exam.total_marks, exam.test_total_marks, 0)}</strong></td>
                          <td>{displayMarkWithTotal(exam.class_avg_total, exam.test_total_marks, 0)}</td>
                          <td className="top-score">{displayMarkWithTotal(exam.top_score_total, exam.test_total_marks, 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={{ color: '#666', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                No monthly test data available
              </p>
            )}
          </div>

          {/* Teachers Feedback & Suggestions */}
          <div className="profile-section">
            <h3>✍️ Teachers Feedback & Suggestions</h3>
            <div className="feedback-section">
              <div className="feedback-item">
                <label>Date:</label>
                <input
                  type="date"
                  value={currentFeedback.date}
                  onChange={(e) => setCurrentFeedback({ ...currentFeedback, date: e.target.value })}
                  className="feedback-date"
                />
              </div>
              <div className="feedback-item">
                <label>Teachers Feedback:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Enter teacher's feedback about the student's performance, behavior, and progress..."
                  rows="4"
                  value={currentFeedback.teacherFeedback}
                  onChange={(e) => setCurrentFeedback({ ...currentFeedback, teacherFeedback: e.target.value })}
                ></textarea>
              </div>
              <div className="feedback-item">
                <label>Suggestions:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Enter suggestions for improvement, areas to focus on..."
                  rows="4"
                  value={currentFeedback.suggestions}
                  onChange={(e) => setCurrentFeedback({ ...currentFeedback, suggestions: e.target.value })}
                ></textarea>
              </div>
              <div className="signature-grid">
                <div className="signature-box">
                  <label>Academic Director's Signature</label>
                  <input
                    type="text"
                    placeholder="Type name as signature"
                    value={currentFeedback.academicDirectorSignature}
                    onChange={(e) => setCurrentFeedback({ ...currentFeedback, academicDirectorSignature: e.target.value })}
                    className="signature-input"
                  />
                  {currentFeedback.academicDirectorSignature && (
                    <div className="signature-preview"><span className="signature-text">{currentFeedback.academicDirectorSignature}</span></div>
                  )}
                </div>
                <div className="signature-box">
                  <label>Student Signature</label>
                  <input
                    type="text"
                    placeholder="Type name as signature"
                    value={currentFeedback.studentSignature}
                    onChange={(e) => setCurrentFeedback({ ...currentFeedback, studentSignature: e.target.value })}
                    className="signature-input"
                  />
                  {currentFeedback.studentSignature && (
                    <div className="signature-preview"><span className="signature-text">{currentFeedback.studentSignature}</span></div>
                  )}
                </div>
                <div className="signature-box">
                  <label>Parents Signature</label>
                  <input
                    type="text"
                    placeholder="Type name as signature"
                    value={currentFeedback.parentSignature}
                    onChange={(e) => setCurrentFeedback({ ...currentFeedback, parentSignature: e.target.value })}
                    className="signature-input"
                  />
                  {currentFeedback.parentSignature && (
                    <div className="signature-preview"><span className="signature-text">{currentFeedback.parentSignature}</span></div>
                  )}
                </div>
              </div>
              <button
                className="btn-save-feedback"
                onClick={handleSaveFeedback}
                disabled={savingFeedback}
              >
                {savingFeedback ? 'Saving...' : '💾 Save Feedback'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback & Suggestions Tab */}
      {activeTab === 'feedback' && (
        <div className="tab-content">
          <div className="profile-section">
            <h3>📜 Feedback History</h3>
            {feedbackList.length === 0 ? (
              <p style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>
                No feedback entries yet.
              </p>
            ) : (
              <div className="feedback-history">
                {feedbackList.map((feedback) => (
                  <div key={feedback.feedback_id || feedback.id} className="feedback-card">
                    <div className="feedback-card-header">
                      <span className="feedback-date-badge">
                        {(feedback.feedback_date || feedback.date)
                          ? new Date(feedback.feedback_date || feedback.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
                          : 'Unknown Date'}
                      </span>
                    </div>
                    <div className="feedback-card-body">
                      {feedback.teacher_feedback && (
                        <div className="feedback-entry">
                          <h4>Teachers Feedback</h4>
                          <p>{feedback.teacher_feedback}</p>
                        </div>
                      )}
                      {feedback.suggestions && (
                        <div className="feedback-entry">
                          <h4>Suggestions</h4>
                          <p>{feedback.suggestions}</p>
                        </div>
                      )}
                      <div className="feedback-signatures" style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
                        {feedback.academic_director_signature && (
                          <div>
                            <span style={{ fontWeight: 600, color: '#718096', fontSize: '13px' }}>Academic Director: </span>
                            <span style={{ fontStyle: 'italic', color: '#5b5fc7' }}>{feedback.academic_director_signature}</span>
                          </div>
                        )}
                        {feedback.student_signature && (
                          <div>
                            <span style={{ fontWeight: 600, color: '#718096', fontSize: '13px' }}>Student: </span>
                            <span style={{ fontStyle: 'italic', color: '#5b5fc7' }}>{feedback.student_signature}</span>
                          </div>
                        )}
                        {feedback.parent_signature && (
                          <div>
                            <span style={{ fontWeight: 600, color: '#718096', fontSize: '13px' }}>Parent: </span>
                            <span style={{ fontStyle: 'italic', color: '#5b5fc7' }}>{feedback.parent_signature}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="student-pdf-export-root" ref={reportExportRef}>
        <div className="student-pdf-page">
          <div className="student-pdf-header">Progress Charts - {displayData.name} | Graavitons</div>
          <h1>Student Progress Charts</h1>
          <p style={{ margin: '0 0 10px', color: '#64748b', fontSize: '14px' }}>
            {isDailyOnlyProgressReport ? 'Unit test performance report' : 'Unit test and monthly test performance report'}
            {formatDateRangeLabel() && (
              <span style={{ display: 'block', marginTop: '4px', color: '#6366f1', fontWeight: 600, fontSize: '13px' }}>
                📅 Date Filter: {formatDateRangeLabel()}
              </span>
            )}
          </p>
          <div className="student-pdf-info-grid">
            <div>
              <p><strong>School:</strong> {displayData.schoolName}</p>
              <p><strong>Name:</strong> {displayData.name}</p>
              <p><strong>Roll No:</strong> {displayData.rollNo}</p>
            </div>
            <div>
              <p><strong>Grade:</strong> {displayData.grade}</p>
              <p><strong>Program:</strong> {reportProgram}</p>
              <p><strong>Series:</strong> {analysisData?.student?.batch_name || 'N/A'}</p>
            </div>
          </div>

          <div className="student-pdf-chart-block">
            <h3>Attendance Summary</h3>
            <p>{isDailyOnlyProgressReport ? 'Unit test attendance (Attended/Conducted)' : 'Weekly and monthly test attendance (Attended/Conducted)'}</p>
            <div className="student-pdf-table-wrap">
              <table className="student-pdf-table attendance-table">
                <thead>
                  <tr>
                    <th>Test Type</th>
                    <th>Conducted</th>
                    <th>Attended</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSummaryRows.map((row) => (
                    <tr key={row.testType}>
                      <td>{row.testType}</td>
                      <td>{row.conducted}</td>
                      <td>{row.attended}</td>
                      <td><strong>{row.summary}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="student-pdf-chart-block student-pdf-chart-panel">
            <h3>Subject Comparison - Unit Test</h3>
            <p>Student subject-wise trend across unit tests ({dailySubjectComparisonReportData.length} points)</p>
            <BarChart
              width={720}
              height={250}
              data={dailySubjectComparisonReportData}
              margin={pdfChartMargin}
              barCategoryGap="40%"
            >
              <CartesianGrid strokeDasharray="0" stroke="#d1d5db" />
              <XAxis dataKey="label" {...pdfXAxisProps} />
              <YAxis {...pdfYAxisProps} />
              <Tooltip />
              <Legend
                {...pdfLegendProps}
                content={renderDailyPdfLegend}
              />
              <Bar
                dataKey="value"
                isAnimationActive={false}
                barSize={34}
                maxBarSize={34}
                radius={[3, 3, 0, 0]}
              >
                {dailySubjectComparisonReportData.map((entry, index) => (
                  <Cell key={`daily-report-cell-${entry.label}-${index}`} fill={entry.fill} />
                ))}
                <LabelList dataKey="value" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'value')} />
              </Bar>
            </BarChart>
          </div>

          {isDailyOnlyProgressReport && (
            <div className="student-pdf-chart-block student-pdf-chart-panel">
              <h3>Total Score vs Class High / Average / Low - Unit Test</h3>
              <p>Unit-test total comparison against class band ({dailyTotalComparisonReportData.length} tests)</p>
              <BarChart width={720} height={250} data={dailyTotalComparisonReportData} margin={pdfChartMargin}>
                <CartesianGrid strokeDasharray="0" stroke="#d1d5db" />
                <XAxis dataKey="label" {...pdfXAxisProps} />
                <YAxis {...pdfYAxisProps} />
                <Tooltip />
                <Legend {...pdfLegendProps} />
                <Bar dataKey="student" isAnimationActive={false} fill="#2563eb" name="Student" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="student" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'student')} />
                </Bar>
                <Bar dataKey="high" isAnimationActive={false} fill="#22c55e" name="High" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="high" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'high')} />
                </Bar>
                <Bar dataKey="average" isAnimationActive={false} fill="#f1ed08" name="Average" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="average" content={renderSmartPdfBarLabel('#111827', '#111827', 'average')} />
                </Bar>
                <Bar dataKey="low" isAnimationActive={false} fill="#ef4444" name="Low" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="low" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'low')} />
                </Bar>
              </BarChart>
            </div>
          )}

          {!isDailyOnlyProgressReport && (
            <div className="student-pdf-chart-block student-pdf-chart-panel">
              <h3>Total Score vs Class High / Average / Low - Monthly Test</h3>
              <p>Monthly total comparison against class band ({reportTests.length} tests)</p>
              <BarChart width={720} height={250} data={totalComparisonReportData} margin={pdfChartMargin}>
                <CartesianGrid strokeDasharray="0" stroke="#d1d5db" />
                <XAxis dataKey="label" {...pdfXAxisProps} />
                <YAxis {...pdfYAxisProps} />
                <Tooltip />
                <Legend {...pdfLegendProps} />
                <Bar dataKey="student" isAnimationActive={false} fill="#2563eb" name="Student" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="student" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'student')} />
                </Bar>
                <Bar dataKey="high" isAnimationActive={false} fill="#22c55e" name="High" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="high" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'high')} />
                </Bar>
                <Bar dataKey="average" isAnimationActive={false} fill="#f1ed08" name="Average" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="average" content={renderSmartPdfBarLabel('#111827', '#111827', 'average')} />
                </Bar>
                <Bar dataKey="low" isAnimationActive={false} fill="#ef4444" name="Low" radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="low" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'low')} />
                </Bar>
              </BarChart>
            </div>
          )}
        </div>

        {isDailyOnlyProgressReport ? (
          <div className="student-pdf-page">
            <div className="student-pdf-header">Progress Charts - {displayData.name} | Graavitons</div>
            {pdfReportSubjectKeys.map((subjectKey) => {
              const subjectLabel = reportSubjectMeta[subjectKey]?.label || subjectKey;
              const chartData = dailySubjectVsClassReportData[subjectKey] || [];

              return (
                <div className="student-pdf-chart-block student-pdf-chart-panel" key={`daily-subject-${subjectKey}`}>
                  <h3>{subjectLabel} Score: Student vs Class</h3>
                  <p>{subjectLabel} unit-test performance vs class High, Average, Low ({chartData.length} tests)</p>
                  <BarChart width={720} height={250} data={chartData} margin={pdfChartMargin}>
                    <CartesianGrid strokeDasharray="0" stroke="#d1d5db" />
                    <XAxis dataKey="label" {...pdfXAxisProps} />
                    <YAxis {...pdfYAxisProps} />
                    <Tooltip />
                    <Legend {...pdfLegendProps} />
                    <Bar dataKey="student" isAnimationActive={false} fill="#2563eb" name="Student" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="student" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'student')} />
                    </Bar>
                    <Bar dataKey="high" isAnimationActive={false} fill="#22c55e" name="High" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="high" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'high')} />
                    </Bar>
                    <Bar dataKey="average" isAnimationActive={false} fill="#f1ed08" name="Average" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="average" content={renderSmartPdfBarLabel('#111827', '#111827', 'average')} />
                    </Bar>
                    <Bar dataKey="low" isAnimationActive={false} fill="#ef4444" name="Low" radius={[3, 3, 0, 0]}>
                      <LabelList dataKey="low" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'low')} />
                    </Bar>
                  </BarChart>
                </div>
              );
            })}
          </div>
        ) : (
        <>
        <div className="student-pdf-page">
          <div className="student-pdf-header">Progress Charts - {displayData.name} | Graavitons</div>
          {reportSubjectKeys.map((subjectKey) => {
            const subjectLabel = reportSubjectMeta[subjectKey]?.label || subjectKey;
            const chartData = mockSubjectVsClassReportData[subjectKey] || [];

            return (
              <div className="student-pdf-chart-block student-pdf-chart-panel" key={`mock-subject-${subjectKey}`}>
                <h3>{subjectLabel} - Monthly Test: Student vs Class</h3>
                <p>{subjectLabel} performance vs class High, Average, Low ({reportTests.length} tests)</p>
                <BarChart width={720} height={250} data={chartData} margin={pdfChartMargin}>
                  <CartesianGrid strokeDasharray="0" stroke="#d1d5db" />
                  <XAxis dataKey="label" {...pdfXAxisProps} />
                  <YAxis {...pdfYAxisProps} />
                  <Tooltip />
                  <Legend {...pdfLegendProps} />
                  <Bar dataKey="student" isAnimationActive={false} fill="#2563eb" name="Student" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="student" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'student')} />
                  </Bar>
                  <Bar dataKey="high" isAnimationActive={false} fill="#22c55e" name="High" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="high" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'high')} />
                  </Bar>
                  <Bar dataKey="average" isAnimationActive={false} fill="#f1ed08" name="Average" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="average" content={renderSmartPdfBarLabel('#111827', '#111827', 'average')} />
                  </Bar>
                  <Bar dataKey="low" isAnimationActive={false} fill="#ef4444" name="Low" radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="low" content={renderSmartPdfBarLabel('#ffffff', '#111827', 'low')} />
                  </Bar>
                </BarChart>
              </div>
            );
          })}
        </div>

        <div className="student-pdf-page">
          <div className="student-pdf-header">Progress Charts - {displayData.name} | Graavitons</div>

          <div className="student-pdf-chart-block">
            <h3>🧩 Per-Test Remediation (Latest 6)</h3>
            <p>Detailed Achievements and Red Flags from latest test events</p>
            <div className="pdf-insight-grid">
              {pdfInsightRows.length > 0 ? pdfInsightRows.map((testInsight, idx) => (
                <div className="pdf-insight-card" key={`pdf-insight-${idx}`}>
                  <div className="pdf-insight-head">
                    <strong>{testInsight.test_label} ({String(testInsight.test_type || '').toUpperCase() || 'TEST'})</strong>
                    <span>{testInsight.test_date ? new Date(testInsight.test_date).toLocaleDateString('en-IN') : 'N/A'}</span>
                  </div>
                  <div className="pdf-insight-subhead">
                    {testInsight.subject ? `${testInsight.subject}` : 'General'}
                    {testInsight.unit_name ? ` • ${testInsight.unit_name}` : ''}
                  </div>
                  <div className="pdf-insight-meta">{renderPdfScoreSummary(testInsight)}</div>
                  <div className="pdf-insight-body">
                    <p className="pdf-insight-section"><strong>Achievement</strong> ({testInsight.achievements?.length || 0})</p>
                    {testInsight.achievements?.[0] ? (
                      <>
                        <p><strong>• {testInsight.achievements[0].title}</strong></p>
                        <p>{testInsight.achievements[0].detail}</p>
                        {renderInsightMetric(testInsight.achievements[0].metric) && (
                          <p><strong>Metric:</strong> {renderInsightMetric(testInsight.achievements[0].metric)}</p>
                        )}
                      </>
                    ) : (
                      <p>No achievement noted for this test.</p>
                    )}

                    <p className="pdf-insight-section"><strong>Red Flag</strong> ({testInsight.red_flags?.length || 0})</p>
                    {testInsight.red_flags?.[0] ? (
                      <>
                        <p><strong>• {testInsight.red_flags[0].title}</strong></p>
                        <p>{testInsight.red_flags[0].detail}</p>
                        {renderInsightMetric(testInsight.red_flags[0].metric) && (
                          <p><strong>Metric:</strong> {renderInsightMetric(testInsight.red_flags[0].metric)}</p>
                        )}
                        {testInsight.red_flags[0].remediation_action && (
                          <p><strong>Plan:</strong> {testInsight.red_flags[0].remediation_action}</p>
                        )}
                      </>
                    ) : (
                      <p>No red flag noted for this test.</p>
                    )}
                  </div>
                </div>
              )) : (
                <div className="pdf-insight-card">No remediation insights available.</div>
              )}
            </div>
          </div>

        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default StudentProfile;
